import { type FC, useState } from "react";

import type { SchemaTelemetryRecord } from "../../types/schemaTelemetry";

import {
  getSchemaTelemetryPromptUrl,
  getSchemaTelemetrySchemaUrl,
} from "../../api/schemaTelemetry";
import { formatRelativeTime } from "../../utils/time";
import { Button } from "../common/Button";
import { ChevronRightIcon } from "../common/icons";
import { JsonViewer } from "../common/JsonViewer";
import { MetricCard } from "../common/MetricCard";
import { Modal } from "../common/Modal";
import { StatusBadge } from "../common/StatusBadge";
import { Text } from "../common/Text";

interface SchemaTelemetryDetailsModalProps {
  record: SchemaTelemetryRecord | null;
  isOpen: boolean;
  onClose: () => void;
  loading: boolean;
}

const DownloadIcon: FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
    />
  </svg>
);

const SNIPPET_CONTAINER_CLASS =
  "relative rounded-none border border-slate-700 bg-slate-900/90 p-3 text-slate-100 shadow-inner dark:border-slate-700 dark:bg-black/80 dark:text-white";
const SNIPPET_CODE_CLASS = "font-mono text-slate-100 dark:text-white/90";
const VIEWER_CONTAINER_CLASS =
  "relative rounded-none border border-slate-700 bg-slate-900 p-3 text-slate-100 dark:text-white";
const VIEWER_CODE_CLASS =
  "font-mono leading-relaxed text-slate-100 dark:text-white/90";
const DETAIL_CARD_CLASS =
  "rounded-none border border-slate-300 p-3 dark:border-slate-700";
const DETAIL_VALUES_CLASS =
  "space-y-1 text-xs text-slate-700 dark:text-slate-300";

function formatOptionalNumber(value: number | null | undefined): string {
  return typeof value === "number" ? String(value) : "N/A";
}

/** Header metrics grid (validation status, schema, provider, errors, callsite). */
const HeaderMetrics: FC<{ record: SchemaTelemetryRecord }> = ({ record }) => (
  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
    <MetricCard
      label="Status"
      value={<StatusBadge status={record.validationStatus} />}
    />
    <MetricCard
      label="Schema"
      value={
        <span className="break-all text-sm">{record.schemaName ?? "N/A"}</span>
      }
    />
    <MetricCard
      label="Provider"
      value={<span className="text-sm">{record.provider ?? "N/A"}</span>}
    />
    <MetricCard
      label="Errors"
      value={
        <span
          className={`text-sm ${record.validationErrorCount > 0 ? "text-rose-600 dark:text-rose-400" : "text-slate-900 dark:text-white"}`}
        >
          {record.validationErrorCount}
        </span>
      }
    />
    <div className="col-span-1 sm:col-span-2 lg:col-span-4">
      <MetricCard
        label="Callsite"
        value={
          <span className="break-all font-mono text-sm">{record.callsite}</span>
        }
      />
    </div>
  </div>
);

/** Status flag pills for parsed result & raw response. */
const StatusFlags: FC<{ record: SchemaTelemetryRecord }> = ({ record }) => {
  const activeClass =
    "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-400";
  const inactiveClass =
    "border-slate-200 bg-slate-100 text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400";

  return (
    <div className="flex flex-wrap gap-3">
      <Text
        variant="label"
        className={`rounded-none border px-3 py-1 ${record.hasParsedResult ? activeClass : inactiveClass}`}
      >
        Parsed Result: {record.hasParsedResult ? "Yes" : "No"}
      </Text>
      <Text
        variant="label"
        className={`rounded-none border px-3 py-1 ${record.hasRawResponse ? activeClass : inactiveClass}`}
      >
        Raw Response: {record.hasRawResponse ? "Yes" : "No"}
      </Text>
    </div>
  );
};

/** Download buttons for prompt and schema. */
const DownloadActions: FC<{ record: SchemaTelemetryRecord }> = ({ record }) => (
  <div className="flex gap-3 border-t border-slate-200 pt-4 dark:border-slate-700">
    <Button
      variant="secondary"
      size="sm"
      leftIcon={<DownloadIcon className="h-4 w-4" />}
      onClick={() =>
        window.open(getSchemaTelemetryPromptUrl(record.id), "_blank")
      }
    >
      Download Prompt
    </Button>
    {record.schemaName && (
      <Button
        variant="secondary"
        size="sm"
        leftIcon={<DownloadIcon className="h-4 w-4" />}
        onClick={() =>
          window.open(getSchemaTelemetrySchemaUrl(record.id), "_blank")
        }
      >
        Download Schema
      </Button>
    )}
  </div>
);

/** Parsed result and raw response JSON snippets. */
const SnippetsSection: FC<{ record: SchemaTelemetryRecord }> = ({ record }) => (
  <div className="space-y-4">
    {record.parsedResultSnippet && (
      <div>
        <h4 className="mb-2 font-mono text-[11px] font-bold uppercase tracking-widest text-slate-900 dark:text-white">
          Parsed Result Snippet
        </h4>
        <JsonViewer
          data={record.parsedResultSnippet}
          defaultMode="pretty"
          maxHeight="max-h-40"
          containerClassName={SNIPPET_CONTAINER_CLASS}
          className={SNIPPET_CODE_CLASS}
        />
      </div>
    )}
    {record.rawResponseSnippet && (
      <div>
        <h4 className="mb-2 font-mono text-[11px] font-bold uppercase tracking-widest text-slate-900 dark:text-white">
          Raw Response Snippet
        </h4>
        <JsonViewer
          data={record.rawResponseSnippet}
          maxHeight="max-h-40"
          containerClassName={SNIPPET_CONTAINER_CLASS}
          className={SNIPPET_CODE_CLASS}
        />
      </div>
    )}
  </div>
);

/** Response metadata card (model, stage, tokens). */
const ResponseMetadataCard: FC<{ record: SchemaTelemetryRecord }> = ({
  record,
}) => (
  <div className={DETAIL_CARD_CLASS}>
    <Text variant="caption" as="h5" className="mb-2">
      Response Metadata
    </Text>
    <div className={DETAIL_VALUES_CLASS}>
      <div>Response ID: {record.responseId ?? "N/A"}</div>
      <div>Response Model: {record.responseModel ?? "N/A"}</div>
      <div>Model Stage: {record.modelStage ?? "N/A"}</div>
      <div>Max Tokens: {formatOptionalNumber(record.maxTokens)}</div>
    </div>
  </div>
);

/** Usage details card (input/output/cached tokens). */
const UsageDetailsCard: FC<{ record: SchemaTelemetryRecord }> = ({
  record,
}) => (
  <div className={DETAIL_CARD_CLASS}>
    <Text variant="caption" as="h5" className="mb-2">
      Usage Details
    </Text>
    <div className={DETAIL_VALUES_CLASS}>
      <div>Input Tokens: {formatOptionalNumber(record.usageInputTokens)}</div>
      <div>Output Tokens: {formatOptionalNumber(record.usageOutputTokens)}</div>
      <div>Cached Tokens: {formatOptionalNumber(record.usageCachedTokens)}</div>
      <div>
        Cached Input Tokens:{" "}
        {formatOptionalNumber(record.usageCachedInputTokens)}
      </div>
      <div>
        Cache Write Tokens: {formatOptionalNumber(record.usageCacheWriteTokens)}
      </div>
    </div>
  </div>
);

/** Collapsible grid of schema viewers + metadata/usage cards. */
const ExtendedDetailsGrid: FC<{ record: SchemaTelemetryRecord }> = ({
  record,
}) => (
  <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
    {record.expectedSchema && (
      <div>
        <Text variant="caption" as="h5" className="mb-1">
          Expected Schema
        </Text>
        <JsonViewer
          data={record.expectedSchema}
          defaultMode="raw"
          maxHeight="max-h-72"
          containerClassName={VIEWER_CONTAINER_CLASS}
          className={VIEWER_CODE_CLASS}
        />
      </div>
    )}
    {record.responseSchema && (
      <div>
        <Text variant="caption" as="h5" className="mb-1">
          Response Schema
        </Text>
        <JsonViewer
          data={record.responseSchema}
          defaultMode="raw"
          maxHeight="max-h-72"
          containerClassName={VIEWER_CONTAINER_CLASS}
          className={VIEWER_CODE_CLASS}
        />
      </div>
    )}
    <ResponseMetadataCard record={record} />
    <UsageDetailsCard record={record} />
  </div>
);

/**
 * SchemaTelemetryDetailsModal
 *
 * Rendering/layout audit:
 * - Header metrics: validation status, schema/provider, error count, callsite.
 * - Status flags: `hasParsedResult` and `hasRawResponse`.
 * - Downloads: prompt (always) and schema (when `schemaName` exists).
 * - Snippets: `parsedResultSnippet` and `rawResponseSnippet` are displayed via JsonViewer (preformatted).
 * - Raw toggle: shows typed schema/response/usage details.
 */
export const SchemaTelemetryDetailsModal: FC<
  SchemaTelemetryDetailsModalProps
> = ({ record, isOpen, onClose, loading }) => {
  const [showRaw, setShowRaw] = useState(false);

  if (!isOpen) {
    return null;
  }

  return (
    <Modal
      title="Schema Telemetry Details"
      subtitle={
        record
          ? `ID: ${record.id} · ${formatRelativeTime(new Date(record.timestamp).getTime())}`
          : undefined
      }
      onClose={onClose}
      widthClass="max-w-4xl"
    >
      <div className="space-y-4">
        {loading && (
          <div className="flex h-40 items-center justify-center">
            <span className="text-sm text-slate-500 dark:text-slate-400">
              Loading details...
            </span>
          </div>
        )}
        {!loading && !record && (
          <div className="flex h-40 items-center justify-center">
            <span className="text-sm text-slate-500 dark:text-slate-400">
              Record not found.
            </span>
          </div>
        )}
        {!loading && record && (
          <>
            <HeaderMetrics record={record} />
            <StatusFlags record={record} />
            <DownloadActions record={record} />
            <SnippetsSection record={record} />

            {/* Raw Data Toggle */}
            <div className="border-t border-slate-200 pt-4 dark:border-slate-700">
              <Button
                type="button"
                onClick={() => setShowRaw(!showRaw)}
                variant="ghost"
                size="sm"
                className="mb-2"
                leftIcon={
                  <ChevronRightIcon
                    size={12}
                    className={`h-3 w-3 transition-transform ${showRaw ? "rotate-90" : ""}`}
                  />
                }
              >
                {showRaw ? "Hide" : "Show"} Extended Typed Details
              </Button>

              {showRaw && <ExtendedDetailsGrid record={record} />}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};
